import { API_ENDPOINTS } from '@/constants/api-endpoints';
import {
  acceptInvitation,
  createWorkspace,
  deleteColdCallContext,
  deleteWorkspace,
  deleteEmailUnsubscribe,
  demoteFromAdmin,
  disableMemberMfa,
  generateCompanyResearch,
  generateCommunityMentionsConfig,
  generateMarketIntelConfig,
  generateJobPostingContext,
  generateJobTitles,
  generateSignalConfigs,
  generateTechnologies,
  generateJoinCode,
  getActiveWorkspace,
  getColdCallContext,
  getCompanyResearch,
  getCreditAnalytics,
  getCreditHistory,
  getEmailUnsubscribes,
  getWorkspaceById,
  getWorkspaceInvitations,
  getWorkspaceMembers,
  getWorkspaceRoles,
  createWorkspaceRole,
  updateWorkspaceRole,
  deleteWorkspaceRole,
  assignMemberRole,
  getWorkspaces,
  joinWorkspace,
  promoteToAdmin,
  removeMember,

  resendInvitation,
  revokeInvitation,
  sendInvitation,
  switchWorkspace,
  updateColdCallContext,
  updateCompanyResearch,
  updateMemberPermissions,
  updateMemberUiPersona,
  updateMyTaskDraftLanguage,
  updateWorkspace,
  type CompanyResearchContext,
  type ColdCallContext,
} from '@/services/workspaces';
import type {
  ISendInvitationRequest,
  IUpdateMemberPermissionsRequest,
  IWorkspace,
  WorkspaceUiPersona,
  TaskDraftLanguagePreference,
} from '@/types/workspace';
import {
  getMarketIntelConfig,
  saveMarketIntelConfig,
  type MarketIntelConfigPayload,
} from '@/services/marketIntel';
import {
  getJobPostingConfigs,
  getJobPostingConfig,
  createJobPostingConfig,
  updateJobPostingConfig,
  deleteJobPostingConfig,
  type JobPostingConfigInput,
} from '@/services/jobPostingConfig';
import {
  getF5BotConfig,
  saveF5BotConfig,
  getLinkedinMentionConfig,
  saveLinkedinMentionConfig,
  getCommunityMentions,
  type F5BotConfigPayload,
  type LinkedinMentionConfigPayload,
  type CommunityMentionsParams,
} from '@/services/f5botConfig';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ====================================================================
// Core Workspace Queries
// ====================================================================

export const useGetWorkspaces = (enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.listWorkspaces],
    queryFn: getWorkspaces,
    enabled,
  });
};

export const useGetActiveWorkspace = (enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getActiveWorkspace],
    queryFn: getActiveWorkspace,
    enabled,
  });
};

export const useGetCompanyResearch = (workspaceId: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getCompanyResearch(workspaceId)],
    queryFn: () => getCompanyResearch(workspaceId),
    enabled: enabled && !!workspaceId,
  });
};

export const useUpdateCompanyResearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, data }: { workspaceId: number; data: CompanyResearchContext }) =>
      updateCompanyResearch(workspaceId, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific workspace research query
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getCompanyResearch(variables.workspaceId)],
      });
    },
  });
};

export const useGenerateCompanyResearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workspaceId: number) => generateCompanyResearch(workspaceId),
    onSuccess: (_, workspaceId) => {
      // Invalidate the specific workspace research query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getCompanyResearch(workspaceId)],
      });
    },
  });
};

export const useGenerateJobPostingContext = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workspaceId: number) => generateJobPostingContext(workspaceId),
    onSuccess: () => {
      // Invalidate job posting context queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ['job-posting-context'],
      });
    },
  });
};

export const useGenerateJobTitles = () => {
  return useMutation({
    mutationFn: (workspaceId: number) => generateJobTitles(workspaceId),
  });
};

export const useGenerateTechnologies = () => {
  return useMutation({
    mutationFn: (workspaceId: number) => generateTechnologies(workspaceId),
  });
};

export const useGenerateSignalConfigs = () => {
  return useMutation({
    mutationFn: (workspaceId: number) => generateSignalConfigs(workspaceId),
  });
};

export const useGenerateCommunityMentionsConfig = () => {
  return useMutation({
    mutationFn: (workspaceId: number) => generateCommunityMentionsConfig(workspaceId),
  });
};

export const useGenerateMarketIntelConfig = () => {
  return useMutation({
    mutationFn: (workspaceId: number) => generateMarketIntelConfig(workspaceId),
  });
};

export const useGetColdCallContext = (workspaceId: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getColdCallContext(workspaceId)],
    queryFn: () => getColdCallContext(workspaceId),
    enabled: enabled && !!workspaceId,
  });
};

export const useUpdateColdCallContext = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, data }: { workspaceId: number; data: ColdCallContext }) =>
      updateColdCallContext(workspaceId, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific workspace cold call context query
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getColdCallContext(variables.workspaceId)],
      });
    },
  });
};

const useDeleteColdCallContext = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workspaceId: number) => deleteColdCallContext(workspaceId),
    onSuccess: (_, workspaceId) => {
      // Invalidate the specific workspace cold call context query to refetch
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getColdCallContext(workspaceId)],
      });
    },
  });
};

// ====================================================================
// Market Intelligence Config
// ====================================================================

export const useGetMarketIntelConfig = (workspaceId?: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.marketIntelConfig, workspaceId],
    queryFn: getMarketIntelConfig,
    enabled: enabled && !!workspaceId,
  });
};

export const useSaveMarketIntelConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      workspaceId,
    }: {
      data: MarketIntelConfigPayload;
      workspaceId?: number;
    }) => {
      // workspaceId is used in onSuccess callback via variables
      void workspaceId;
      return saveMarketIntelConfig(data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.marketIntelConfig, variables.workspaceId],
      });
    },
  });
};

// ====================================================================
// F5Bot Community Mentions Config
// ====================================================================

export const useGetF5BotConfig = (workspaceId?: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.f5botConfigs, workspaceId],
    queryFn: getF5BotConfig,
    enabled: enabled && !!workspaceId,
  });
};

export const useSaveF5BotConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      workspaceId,
    }: {
      data: F5BotConfigPayload;
      workspaceId?: number;
    }) => {
      void workspaceId;
      return saveF5BotConfig(data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.f5botConfigs, variables.workspaceId],
      });
    },
  });
};

// ====================================================================
// LinkedIn Mention Config
// ====================================================================

export const useGetLinkedinMentionConfig = (workspaceId?: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.linkedinMentionConfigs, workspaceId],
    queryFn: getLinkedinMentionConfig,
    enabled: enabled && !!workspaceId,
  });
};

export const useSaveLinkedinMentionConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      workspaceId,
    }: {
      data: LinkedinMentionConfigPayload;
      workspaceId?: number;
    }) => {
      void workspaceId;
      return saveLinkedinMentionConfig(data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.linkedinMentionConfigs, variables.workspaceId],
      });
    },
  });
};

export const useGetCommunityMentions = (params: CommunityMentionsParams, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.communityMentions, params],
    queryFn: () => getCommunityMentions(params),
    enabled,
    staleTime: 60_000,
  });
};

// ====================================================================
// Job Posting Config
// ====================================================================

export const useGetJobPostingConfigs = (workspaceId?: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.jobPostingConfigs, workspaceId],
    queryFn: getJobPostingConfigs,
    enabled: enabled && !!workspaceId,
  });
};

const useGetJobPostingConfig = (configId: number, workspaceId?: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.jobPostingConfigs, configId, workspaceId],
    queryFn: () => getJobPostingConfig(configId),
    enabled: enabled && !!configId && !!workspaceId,
  });
};

export const useCreateJobPostingConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: JobPostingConfigInput) => createJobPostingConfig(data),
    onSuccess: () => {
      // Invalidate all job posting config queries (React Query will match by prefix)
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.jobPostingConfigs],
      });
    },
  });
};

export const useUpdateJobPostingConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ configId, data }: { configId: number; data: JobPostingConfigInput }) =>
      updateJobPostingConfig(configId, data),
    onSuccess: (_, variables) => {
      // Invalidate all job posting config queries (React Query will match by prefix)
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.jobPostingConfigs],
      });
      // Also invalidate specific config query
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.jobPostingConfigs, variables.configId],
      });
    },
  });
};

export const useDeleteJobPostingConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configId: number) => deleteJobPostingConfig(configId),
    onSuccess: () => {
      // Invalidate all job posting config queries (React Query will match by prefix)
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.jobPostingConfigs],
      });
    },
  });
};

const useGetWorkspaceById = (workspaceId: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getWorkspace(workspaceId)],
    queryFn: () => getWorkspaceById(workspaceId),
    enabled: enabled && !!workspaceId,
  });
};

// ====================================================================
// Core Workspace Mutations
// ====================================================================

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: () => {
      // Invalidate workspace list to refetch
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, data }: { workspaceId: number; data: Partial<IWorkspace> }) =>
      updateWorkspace(workspaceId, data),
    onSuccess: (_, variables) => {
      // Invalidate specific workspace and list
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
      });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

// ====================================================================
// Workspace Switching & Joining
// ====================================================================

export const useSwitchWorkspace = () => {
  return useMutation({
    mutationFn: switchWorkspace,
    // Remove onSuccess - let WorkspaceProvider handle comprehensive invalidation
    // This prevents duplicate invalidation and race conditions
  });
};

export const useJoinWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: joinWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

// ====================================================================
// Member Management
// ====================================================================

export const useGetWorkspaceMembers = (workspaceId: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getWorkspaceMembers(workspaceId)],
    queryFn: () => getWorkspaceMembers(workspaceId),
    enabled: enabled && !!workspaceId,
  });
};

export const useGetWorkspaceRoles = (workspaceId: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getWorkspaceRoles(workspaceId)],
    queryFn: () => getWorkspaceRoles(workspaceId),
    enabled: enabled && !!workspaceId,
    staleTime: 5 * 60 * 1000, // presets rarely change
  });
};

// Custom-role builder mutations (#2723). Invalidate roles + members on success.
export const useCreateWorkspaceRole = (workspaceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; permissions: string[] }) => createWorkspaceRole(workspaceId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getWorkspaceRoles(workspaceId)] }),
  });
};

export const useUpdateWorkspaceRole = (workspaceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, data }: { roleId: string; data: { name?: string; permissions?: string[] } }) =>
      updateWorkspaceRole(workspaceId, roleId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getWorkspaceRoles(workspaceId)] }),
  });
};

export const useDeleteWorkspaceRole = (workspaceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => deleteWorkspaceRole(workspaceId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getWorkspaceRoles(workspaceId)] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getWorkspaceMembers(workspaceId)] });
    },
  });
};

export const useAssignMemberRole = (workspaceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, roleId }: { memberId: string; roleId: string }) =>
      assignMemberRole(workspaceId, memberId, roleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getWorkspaceMembers(workspaceId)] }),
  });
};

export const useUpdateMemberPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      memberId,
      data,
    }: {
      workspaceId: number;
      memberId: string;
      data: IUpdateMemberPermissionsRequest;
    }) => updateMemberPermissions(workspaceId, memberId, data),
    onSuccess: (_, variables) => {
      // Invalidate members list to reflect changes
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
      // CRITICAL: Invalidate active workspace to refresh current user's permissions
      // This prevents stale admin permissions after self-demotion
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });
    },
  });
};

export const useUpdateMemberUiPersona = (workspaceId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      uiPersona,
    }: {
      memberId: string;
      uiPersona: WorkspaceUiPersona;
    }) => updateMemberUiPersona(workspaceId, memberId, uiPersona),
    onSuccess: async () => {
      // A workspace admin may select their own persona, so refresh the current
      // membership context as well as the team roster.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getWorkspaceMembers(workspaceId)],
        }),
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getActiveWorkspace],
        }),
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getWorkspace(workspaceId)],
        }),
      ]);
    },
  });
};

export const useUpdateMyTaskDraftLanguage = (workspaceId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskDraftLanguagePreference,
    }: {
      taskDraftLanguagePreference: TaskDraftLanguagePreference;
    }) => updateMyTaskDraftLanguage(workspaceId, taskDraftLanguagePreference),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getActiveWorkspace],
        }),
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getWorkspace(workspaceId)],
        }),
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getWorkspaceMembers(workspaceId)],
        }),
      ]);
    },
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, memberId }: { workspaceId: number; memberId: string }) =>
      removeMember(workspaceId, memberId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
      });
    },
  });
};

// useRequestCreditAccess removed (#2711): credit eligibility is now role-based.

// ====================================================================
// Admin Management (Multi-Admin Feature)
// ====================================================================

export const usePromoteToAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, userId }: { workspaceId: number; userId: string }) =>
      promoteToAdmin(workspaceId, userId),
    onSuccess: (_, variables) => {
      // Invalidate members list to reflect admin status changes
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
      // Invalidate active workspace to refresh current user's permissions if needed
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });
      // Invalidate workspace details to refresh admin count
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
      });
    },
  });
};

export const useDemoteFromAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, userId }: { workspaceId: number; userId: string }) =>
      demoteFromAdmin(workspaceId, userId),
    onSuccess: (_, variables) => {
      // Invalidate members list to reflect admin status changes
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
      // CRITICAL: Invalidate active workspace to refresh current user's permissions
      // This prevents stale admin permissions after self-demotion
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });
      // Invalidate workspace details to refresh admin count
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
      });
    },
  });
};

// ====================================================================
// MFA Management (Admin-Only)
// ====================================================================

export const useDisableMemberMfa = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, memberId }: { workspaceId: number; memberId: string }) =>
      disableMemberMfa(workspaceId, memberId),
    onSuccess: (_, variables) => {
      // Invalidate members list to reflect MFA status changes
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
    },
  });
};

// ====================================================================
// Credit Management
// ====================================================================

export const useGetCreditHistory = (
  params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    creditType?: 'free' | 'paid' | 'ai' | 'video_avatar';
    feature?: string;
  },
  workspaceId?: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getCreditHistory, params, workspaceId],
    queryFn: () => getCreditHistory(params, workspaceId),
    enabled,
  });
};

export const useGetCreditAnalytics = (
  params?: {
    startDate?: string;
    endDate?: string;
  },
  workspaceId?: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getCreditAnalytics, params, workspaceId],
    queryFn: () => getCreditAnalytics(params, workspaceId),
    enabled,
  });
};

// ====================================================================
// Invitation Management
// ====================================================================

export const useSendInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, workspaceId }: { data: ISendInvitationRequest; workspaceId?: number }) =>
      sendInvitation(data, workspaceId),
    onSuccess: (_, variables) => {
      if (variables.workspaceId) {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getWorkspaceInvitations(variables.workspaceId)],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getInvitations],
        });
      }
    },
  });
};

export const useGetWorkspaceInvitations = (workspaceId?: number, enabled = true) => {
  const key = workspaceId
    ? [API_ENDPOINTS.getWorkspaceInvitations(workspaceId)]
    : [API_ENDPOINTS.getInvitations];

  return useQuery({
    queryKey: key,
    queryFn: () => getWorkspaceInvitations(workspaceId),
    enabled,
  });
};

export const useRevokeInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      // Invalidate all invitation queries
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getInvitations],
      });
      // Also invalidate workspace-specific invitations
      queryClient.invalidateQueries({
        predicate: (query) =>
          !!(
            query.queryKey[0] &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('/workspaces/') &&
            query.queryKey[0].includes('/invitations')
          ),
      });
    },
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resendInvitation,
    onSuccess: () => {
      // Invalidate all invitation queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getInvitations],
      });
      // Also invalidate workspace-specific invitations
      queryClient.invalidateQueries({
        predicate: (query) =>
          !!(
            query.queryKey[0] &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('/workspaces/') &&
            query.queryKey[0].includes('/invitations')
          ),
      });
    },
  });
};

const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acceptInvitation,
    onSuccess: () => {
      // Invalidate workspace lists since user joined a new workspace
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

// ====================================================================
// Join Code Management
// ====================================================================

export const useGenerateJoinCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateJoinCode,
    onSuccess: (_, workspaceId) => {
      // Invalidate workspace data to refresh the join code
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(workspaceId)],
      });
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });
    },
  });
};

// ====================================================================
// Email Unsubscribes
// ====================================================================

export const useGetEmailUnsubscribes = (
  params: { page?: number; limit?: number; email?: string },
  workspaceId?: number,
  enabled = true,
) =>
  useQuery({
    queryKey: [API_ENDPOINTS.getEmailUnsubscribes(workspaceId ?? 0), params],
    queryFn: () => getEmailUnsubscribes(params, workspaceId),
    enabled: enabled && !!workspaceId,
  });

export const useDeleteEmailUnsubscribe = (workspaceId?: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteEmailUnsubscribe(id, workspaceId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getEmailUnsubscribes(workspaceId ?? 0)],
      });
    },
  });
};
